import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBug,
  faTimes,
  faMapMarkerAlt,
  faCommentAlt,
  faPaperPlane,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import css from './ReportIssueModal.module.css';

interface Props {
  onClose: () => void;
}

export default function ReportIssueModal({ onClose }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!location.trim() || !description.trim()) {
      showToast('Please fill in both location and description fields.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const now = new Date();
      const res = await api.post('/api/issue-tickets', {
        username: user?.username ?? 'Anonymous',
        submissionDate: now.toISOString().split('T')[0],
        submissionTime: now.toTimeString().split(' ')[0],
        location: location.trim(),
        issueDescription: description.trim(),
        status: 'New Ticket',
      });

      showToast(`Issue reported successfully! Ticket ID: ${res.data.ticketId}`, 'success');
      onClose();
    } catch {
      showToast('Failed to submit issue report. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function handleOverlay(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div className={css.overlay} onClick={handleOverlay}>
      <div className={css.modal}>
        <div className={css.header}>
          <h4 className={css.title}>
            <FontAwesomeIcon icon={faBug} /> Report Issue
          </h4>
          <button className={css.close} onClick={onClose} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={css.body}>
            <div className={css.formGroup}>
              <label className={css.label}>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Location *
              </label>
              <input
                type="text"
                className={css.input}
                placeholder="e.g., Dashboard page, Filter section, etc."
                maxLength={200}
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <small className={css.hint}>Describe where you encountered the issue</small>
            </div>

            <div className={css.formGroup}>
              <label className={css.label}>
                <FontAwesomeIcon icon={faCommentAlt} /> Issue Description *
              </label>
              <textarea
                className={css.input}
                rows={4}
                placeholder="Please describe the issue in detail..."
                maxLength={1000}
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <small className={css.hint}>
                Provide as much detail as possible to help us resolve the issue
              </small>
            </div>
          </div>

          <div className={css.footer}>
            <button type="button" className={css.btnCancel} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} /> Cancel
            </button>
            <button type="submit" className={css.btnSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} /> Submit Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
